import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTooltipTip } from '../../hooks/useTooltipTip';
import './DismissableTip.css';

interface DismissableTipProps {
  tipId: string;
  content: string;
  children: React.ReactElement;
  position?: 'top' | 'bottom' | 'left' | 'right';
  wrapperClassName?: string;
  wrapperStyle?: React.CSSProperties;
  disabled?: boolean;
}

export const DismissableTip: React.FC<DismissableTipProps> = ({
  tipId,
  content,
  children,
  position = 'bottom',
  wrapperClassName = '',
  wrapperStyle,
  disabled = false,
}) => {
  const { isVisible, dismiss } = useTooltipTip(tipId);
  const targetRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number; arrowLeft?: string } | null>(null);

  useEffect(() => {
    if (!isVisible || disabled) return;

    let animationFrameId: number;

    const updateCoords = () => {
      if (targetRef.current) {
        const rect = targetRef.current.getBoundingClientRect();
        const scrollX = window.scrollX || window.pageXOffset;
        const scrollY = window.scrollY || window.pageYOffset;
        const viewportWidth = window.innerWidth;

        let top = 0;
        const targetCenter = rect.left + rect.width / 2 + scrollX;
        let left = targetCenter;

        if (position === 'bottom') {
          top = rect.bottom + scrollY + 12;
        } else if (position === 'top') {
          top = rect.top + scrollY - 12;
        } else if (position === 'left') {
          top = rect.top + rect.height / 2 + scrollY;
          left = rect.left + scrollX - 12;
        } else if (position === 'right') {
          top = rect.top + rect.height / 2 + scrollY;
          left = rect.right + scrollX + 12;
        }

        // Clamp bubble left coordinate to prevent screen boundary overflow
        let bubbleWidth = 260; // fallback max-width
        if (bubbleRef.current) {
          bubbleWidth = bubbleRef.current.getBoundingClientRect().width;
        }

        if (position === 'top' || position === 'bottom') {
          const minLeft = bubbleWidth / 2 + 10 + scrollX;
          const maxLeft = viewportWidth - (bubbleWidth / 2) - 10 + scrollX;
          left = Math.max(minLeft, Math.min(maxLeft, left));
        }

        const arrowLeft = (position === 'top' || position === 'bottom')
          ? `${Math.max(10, Math.min(90, 50 + ((targetCenter - left) / bubbleWidth) * 100))}%`
          : undefined;

        setCoords((prev) => {
          if (
            prev &&
            prev.top === top &&
            prev.left === left &&
            prev.arrowLeft === arrowLeft
          ) {
            return prev;
          }
          return { top, left, arrowLeft };
        });
      }

      animationFrameId = requestAnimationFrame(updateCoords);
    };

    updateCoords();

    // Recalcular posição em eventos de redimensionamento e rolagem
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [isVisible, disabled, position]);

  if (!isVisible || disabled) {
    return children;
  }

  const tooltipElement = (
    <div
      ref={bubbleRef}
      className={`dismissable-tip-bubble position-${position}`}
      style={{
        position: 'absolute',
        top: coords ? `${coords.top}px` : '-9999px',
        left: coords ? `${coords.left}px` : '-9999px',
        ...((coords && coords.arrowLeft) ? { '--arrow-left': coords.arrowLeft } as React.CSSProperties : {}),
      }}
    >
      <p className="dismissable-tip-content">{content}</p>
      <div className="dismissable-tip-actions">
        <button className="dismissable-tip-btn" onClick={dismiss}>
          Entendi
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div
        ref={targetRef}
        className={`dismissable-tip-wrapper ${wrapperClassName}`}
        style={{ display: 'inline-block', ...wrapperStyle }}
      >
        {children}
      </div>
      {createPortal(tooltipElement, document.body)}
    </>
  );
};
